import { useState, useRef } from "react";
import * as XLSX from "xlsx";
import TemplateImportEvolution from "./TemplateImportEvolution";
import { usePostApi } from "../../hooks/post_api_hook/usePostApi";
import { url } from "../../utils/confserveur";
import { niveauDe } from "../../utils/campagneEvalNat";
import { Upload, FileSpreadsheet, CheckCircle, AlertTriangle, X, ChevronDown, History } from "lucide-react";

const getCurrentYear = () => new Date().getFullYear() - 1;

// Fonction pour lire les données Excel
const processExcelFile = async (file) => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: "array" });
  const sheets = {};

  workbook.SheetNames.forEach((sheetName) => {
    const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
    sheets[sheetName] = sheetData;
  });

  return sheets;
};
export const validateHeadersEvolution = (headersRow, requiredHeaders) => {
  
    // Normaliser les en-têtes extraites du fichier Excel
    let headers = ""
    try {
         headers = headersRow.map((header) => header?.trim().toLowerCase() || "");
    }
    catch{
        return false
    }
    // Normaliser les en-têtes requises
    const requiredHeadersNormalized = requiredHeaders.map((header) =>
      header.trim().toLowerCase()
    );
  
  
    // Vérifier si tous les en-têtes requis sont présents et dans l'ordre attendu
    const isValid = requiredHeadersNormalized.every(
      (requiredHeader, index) => headers[index] === requiredHeader
    );
  
   
  
    return isValid;
  };

// Fonction helper pour valider une feuille
const validateSheet = (sheetData, sheetName, questions, typeLabel, selectedYear, results, resultKey) => {
  const expectedHeaders = ["question", "id", "nom label", `% réussite ${selectedYear}`];

  if (!validateHeadersEvolution(sheetData[1], expectedHeaders)) {
    results.errors.push(
      `Les en-têtes de la feuille '${sheetName}' ne sont pas valides. Veuillez télécharger le template proposé et copier vos données sur ce template.`
    );
    return;
  }

  // Filtrer les questions selon le typeLabel (peut être une fonction pour Compréhension écrite)
  const questionIds = typeof typeLabel === 'function'
    ? questions.filter(typeLabel).map((q) => q.id)
    : questions.filter((q) => q.type_label === typeLabel).map((q) => q.id);

  sheetData.slice(2).forEach((row, index) => {
    if (!questionIds.includes(row[1])) {
      results.errors.push(`Ligne ${index + 2} (${sheetName}) : Question ID ${row[1]} non valide.`);
    } else if (typeof row[3] !== "number" || isNaN(row[3])) {
      results.errors.push(`Ligne ${index + 3} (${sheetName}) : Le pourcentage (${row[3]}) est manquant ou invalide. Vous devez juste mettre un nombre.`);
    } else if (row[3] < 0 || row[3] > 100) {
      results.errors.push(`Ligne ${index + 3} (${sheetName}) : Le pourcentage (${row[3]}) doit être compris entre 0 et 100.`);
    } else {
      const percentage = row[3] > 1 ? row[3] / 100 : row[3];
      results[resultKey].push([row[0], row[1], row[2], percentage]);
    }
  });
};

const validateData = (sheets, questions, selectedYear, selectedMatiere, niveau) => {
  const results = {
    automatismes: [],
    problemes: [],
    lexique: [],
    comprehensionEcrite: [],
    competencesLangagieres: [],
    errors: [],
  };

  if (selectedMatiere === 'maths') {
    // ===== MATHS =====
    // Validation Automatismes
    if (sheets["Automatismes"]) {
      validateSheet(sheets["Automatismes"], "Automatismes", questions, "Automatisme", selectedYear, results, "automatismes");
    } else {
      results.errors.push("La feuille 'Automatismes' est absente.");
    }

    // Validation Résolution de problèmes (seulement 6ème et 4ème)
    if (niveau[0] === "6" || niveau[0] === "4") {
      if (sheets["Résolution de problèmes"]) {
        validateSheet(sheets["Résolution de problèmes"], "Résolution de problèmes", questions, "Résolution de problèmes", selectedYear, results, "problemes");
      } else {
        results.errors.push("La feuille 'Résolution de problèmes' est absente.");
      }
    }

  } else if (selectedMatiere === 'français') {
    // ===== FRANÇAIS =====
    if (niveau[0] === "5") {
      // Français 5ème : Compétences langagières uniquement
      if (sheets["Compétences langagières"]) {
        validateSheet(sheets["Compétences langagières"], "Compétences langagières", questions, "Compétences langagières et linguistiques", selectedYear, results, "competencesLangagieres");
      } else {
        results.errors.push("La feuille 'Compétences langagières' est absente.");
      }
    } else {
      // Français 6ème et 4ème : Lexique + Compréhension écrite
      if (sheets["Lexique"]) {
        validateSheet(sheets["Lexique"], "Lexique", questions, "Lexique", selectedYear, results, "lexique");
      } else {
        results.errors.push("La feuille 'Lexique' est absente.");
      }

      if (sheets["Compréhension écrite"]) {
        // Pour Compréhension écrite, utiliser une fonction de filtre car le type_label commence par "Compréhension de l'écrit"
        validateSheet(
          sheets["Compréhension écrite"],
          "Compréhension écrite",
          questions,
          (q) => q.type_label?.startsWith("Compréhension de l'écrit"),
          selectedYear,
          results,
          "comprehensionEcrite"
        );
      } else {
        results.errors.push("La feuille 'Compréhension écrite' est absente.");
      }
    }
  }

  return results;
};

const ImportEvolution = ({ availableYears, EvalsNatGroupe, niveau , etablissement, handleReload, selectedMatiere, onClose }) => {

  const [selectedYear, setSelectedYear] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const [, doPost] = usePostApi(url + "evaluations_nat/");
  

  // Étape 1 : Filtrer les objets par le niveau ET par matière
  const filteredData = EvalsNatGroupe.find((item) =>
    niveauDe(item) === niveau[0] && // évaluation par classe ou campagne (D9)
    item.matiere === selectedMatiere
  );

  // Étape 2 : Récupérer les questions du premier objet trouvé
  const questions = filteredData ? filteredData.questions : [];

  // Filtrer les années manquantes dans les données
  const missingYears = Array.from({ length: getCurrentYear() - 2021 }, (_, i) => 2022 + i).filter(
    (year) => !availableYears.includes(year)
  );

  // Fonctions drag & drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
      handleFiles(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
      handleFiles(e.target.files[0]);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFiles = async (file) => {
    setPreviewData(null);
    if (!file || !selectedYear) return;
    const sheets = await processExcelFile(file);
    const validation = validateData(sheets, questions, selectedYear, selectedMatiere, niveau);
    setValidationResults(validation);
    if(validation.errors.length === 0) {
        setPreviewData(sheets);
    }
  };
  
  const handleSubmit = async () => {
    try {
      if (!selectedYear || !validationResults) {
        alert("Veuillez sélectionner une année et importer des données valides.");
        return;
      }

      // Étape 1 : Formatter `stats` selon la matière
      const stats = {
        niveau: niveau,
      };

      if (selectedMatiere === 'maths') {
        // Maths : Automatismes + Problèmes (si 6ème ou 4ème)
        stats.Automatismes = validationResults.automatismes.map((row) => ({
          [row[1]]: row[3],
        }));
        if (niveau[0] === "6" || niveau[0] === "4") {
          stats.Problèmes = validationResults.problemes.map((row) => ({
            [row[1]]: row[3],
          }));
        }
      } else if (selectedMatiere === 'français') {
        // Français selon le niveau
        if (niveau[0] === "5") {
          // 5ème : Compétences langagières
          stats.CompetencesLangagieres = validationResults.competencesLangagieres.map((row) => ({
            [row[1]]: row[3],
          }));
        } else {
          // 6ème et 4ème : Lexique + Compréhension écrite
          stats.Lexique = validationResults.lexique.map((row) => ({
            [row[1]]: row[3],
          }));
          stats.ComprehensionEcrite = validationResults.comprehensionEcrite.map((row) => ({
            [row[1]]: row[3],
          }));
        }
      }

      // Étape 2 : Préparer les données pour l'API
      const payload = {
        etablissement_id: etablissement.id,
        session: selectedYear,
        stats: stats,
        niveau: niveau,
        matiere: selectedMatiere
      };

      const endpoint = url + "evaluations_nat/create_anterior_evaluation_nat/";

      // Étape 3 : Envoyer la requête à l'API
      const result = await doPost(payload, endpoint);

      // Vérifier le résultat de la requête
      if (result && !result.error) {
        alert("Évaluation nationale ancienne créée avec succès !");
        setValidationResults(null);
        handleReload();
        onClose(); // Fermer le modal après succès
      } else {
        alert("Erreur lors de la création de l'évaluation : " + (result?.error || "Erreur inconnue"));
      }
    } catch (error) {
      console.error("Erreur lors de la soumission :", error);
      alert("Une erreur s'est produite : " + (error.message || "Veuillez réessayer."));
    }
  };


  const handleYearChange = (year) => {
        setValidationResults(null)
        setPreviewData(null)
        setSelectedFile(null)
        setSelectedYear(parseInt(year))
  }

  const renderPreviewDataSection = () => {
    const totalRows = Object.keys(previewData).reduce((acc, sheetName) => {
      return acc + (previewData[sheetName].length - 1);
    }, 0);

    return (
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></span>
          Prévisualisation ({totalRows} lignes)
        </h3>

        {Object.keys(previewData).map((sheetName) => (
          <div key={sheetName} className="mb-6">
            <h4 className="text-base font-medium text-slate-700 mb-3">{sheetName}</h4>
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-lg">
              <div className="overflow-x-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 text-white">
                    <tr>
                      {previewData[sheetName][0]?.map((header, idx) => (
                        <th key={idx} className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {previewData[sheetName].slice(1).map((row, idx) => (
                      <tr key={idx} className="hover:bg-cyan-50/50 transition-colors duration-200">
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} className="px-4 py-3 text-slate-700">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderValidationErrors = () => (
    <div className="mt-6 relative">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-red-300 to-rose-300 rounded-2xl blur opacity-30" />
      <div className="relative bg-gradient-to-r from-red-50 to-rose-50 border border-red-200/50 rounded-2xl p-5 overflow-auto max-h-64">
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 bg-red-100 rounded-xl">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <p className="text-lg font-semibold text-red-800">Erreurs de validation</p>
        </div>
        <ul className="text-red-700 text-sm space-y-2 ml-11">
          {validationResults.errors.map((error, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></span>
              {error}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderSuccessMessage = () => (
    <div className="w-full flex justify-center">
      <div className="w-fit bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header avec gradient vert */}
        <div className="relative overflow-hidden bg-gradient-to-r from-emerald-500 to-green-500 px-8 py-10">
          {/* Bouton fermer */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 p-2 bg-gradient-to-r from-amber-500 to-orange-500 backdrop-blur-sm rounded-xl text-white hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-orange-500/30 transition-all"
            >
              <X size={20} />
            </button>
          )}

          {/* Blobs animés */}
          <svg className="absolute top-0 left-0 w-full h-full opacity-30" viewBox="0 0 200 200">
            <circle cx="20" cy="20" r="40" fill="#86efac">
              <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
              <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
            </circle>
            <circle cx="180" cy="80" r="30" fill="#bbf7d0">
              <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
              <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
            </circle>
          </svg>

          <div className="relative z-10 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                <CheckCircle className="text-white" size={40} />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Évolution disponible !
            </h1>
            <p className="text-emerald-100 text-lg">
              Toutes les années ont déjà été importées
            </p>
          </div>
        </div>

        <div className="p-8 text-center">
          <p className="text-slate-600">
            Toutes les évaluations nationales disponibles pour ce niveau ont été importées.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {missingYears.length > 0 ? (
        <div className="w-full flex justify-center">
          <div className="w-fit bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header avec gradient et blobs animés */}
            <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-8 py-10">
              {/* Bouton fermer */}
              {onClose && (
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 z-20 p-2 bg-gradient-to-r from-amber-500 to-orange-500 backdrop-blur-sm rounded-xl text-white hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-orange-500/30 transition-all"
                >
                  <X size={20} />
                </button>
              )}

              {/* Blobs animés */}
              <svg className="absolute top-0 left-0 w-full h-full opacity-30" viewBox="0 0 200 200">
                <circle cx="20" cy="20" r="40" fill="#67e8f9">
                  <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
                </circle>
                <circle cx="180" cy="80" r="30" fill="#a5f3fc">
                  <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
                </circle>
                <circle cx="100" cy="150" r="35" fill="#22d3ee">
                  <animate attributeName="cx" values="100;130;100" dur="10s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="150;170;150" dur="5s" repeatCount="indefinite" />
                </circle>
              </svg>

              <div className="relative z-10 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                    <History className="text-white" size={40} />
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Importer une année antérieure
                </h1>
                <p className="text-cyan-100 text-lg">
                  Importez le fichier XLSX des résultats pour comparer
                </p>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-8">
              {/* Sélecteur d'année */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200/50 rounded-2xl">
                <label className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></span>
                  Année à importer
                </label>
                <div className="relative mt-2">
                  <select
                    className="appearance-none w-full bg-white/80 border border-slate-200 text-slate-800 text-base
                    rounded-xl focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-300 block pl-4 pr-10 py-3
                    transition-all duration-200 hover:shadow-md cursor-pointer font-medium"
                    value={selectedYear || ""}
                    onChange={(e) => handleYearChange(e.target.value)}
                  >
                    <option value="" disabled>
                      Sélectionner une année
                    </option>
                    {missingYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 pointer-events-none" />
                </div>
              </div>

              {selectedYear && (
                <>
                  {/* Zone de drag & drop */}
                  <div
                    className={`relative group border-2 border-dashed rounded-2xl p-8 mb-6 transition-all duration-300 cursor-pointer ${
                      dragOver
                        ? 'border-cyan-500 bg-cyan-50 scale-[1.02]'
                        : selectedFile && validationResults?.errors.length === 0
                          ? 'border-emerald-500 bg-emerald-50'
                          : validationResults?.errors.length > 0
                            ? 'border-red-400 bg-red-50'
                            : 'border-slate-300 bg-slate-50 hover:border-cyan-400 hover:bg-cyan-50/50'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={openFileDialog}
                  >
                    {/* Glow effect */}
                    <div className={`absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl blur opacity-0
                      ${dragOver ? 'opacity-30' : 'group-hover:opacity-20'} transition duration-300`} />

                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileInputChange}
                      accept=".xlsx"
                      className="hidden"
                    />

                    <div className="relative text-center">
                      <div className="flex justify-center mb-4">
                        <div className={`p-4 rounded-2xl transition-all duration-300 ${
                          selectedFile && validationResults?.errors.length === 0
                            ? 'bg-emerald-100'
                            : validationResults?.errors.length > 0
                              ? 'bg-red-100'
                              : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                        }`}>
                          {selectedFile && validationResults?.errors.length === 0 ? (
                            <CheckCircle size={32} className="text-emerald-600" />
                          ) : validationResults?.errors.length > 0 ? (
                            <AlertTriangle size={32} className="text-red-500" />
                          ) : (
                            <Upload size={32} className="text-white" />
                          )}
                        </div>
                      </div>

                      <p className="text-lg font-semibold text-slate-700 mb-2">
                        {selectedFile
                          ? `Fichier: ${selectedFile.name}`
                          : 'Glissez votre fichier XLSX ici'
                        }
                      </p>
                      <p className="text-sm text-slate-500">
                        {selectedFile
                          ? validationResults?.errors.length === 0
                            ? 'Fichier prêt pour l\'import !'
                            : 'Erreurs détectées - Voir ci-dessous'
                          : 'ou cliquez pour parcourir'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Bouton choisir fichier */}
                  <button
                    onClick={openFileDialog}
                    className="w-full mb-6 py-4 px-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-lg font-semibold
                      rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40
                      transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                      flex items-center justify-center gap-3"
                  >
                    <FileSpreadsheet size={22} />
                    Choisir un fichier XLSX
                  </button>

                  {/* Template */}
                  <div className="text-center mb-6">
                    <TemplateImportEvolution questions={questions} selectedYear={selectedYear} niveau={niveau} selectedMatiere={selectedMatiere} />
                  </div>

                  {/* Messages d'erreur */}
                  {validationResults?.errors.length > 0 && renderValidationErrors()}

                  {/* Prévisualisation */}
                  {previewData && validationResults?.errors.length === 0 && (
                    <>
                      {renderPreviewDataSection()}

                      {/* Bouton submit */}
                      <button
                        onClick={handleSubmit}
                        className="w-full mt-6 py-4 px-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-lg font-semibold
                          rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40
                          transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                          flex items-center justify-center gap-3"
                      >
                        <CheckCircle size={22} />
                        Enregistrer les résultats
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        renderSuccessMessage()
      )}
    </div>
  );
};

export default ImportEvolution;